import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';

interface PrizeDetail {
    type: string;
    description: string;
    count: Money;
}

interface WithdrawDetail {
    account: string;
    status: string;
    time: string;
    count: Money;
}

/** 邀请奖金相关数据 */
export interface PrizeState {
    /** 导航信息 */
    nav: string;
    /** 是否显示小程序码 */
    mpVisible: boolean;
    /** 二维码 */
    withdrawType: 'yunzhanghu' | 'shenbianyun';
    /** 账号信息 */
    accountInfo: {
        alipay: string | null,
        bank_card: string | null,
        id_card: string | null,
        name: string | null,
    } | null;
    prizeDetail: ListWrap<PrizeDetail> & ListRequesting;
    withdrawDetail: ListWrap<WithdrawDetail> & ListRequesting;
    checkContract: {
        need_contract: boolean, // 是否需要签约
        activity_ids: number[], // 需要签约的活动id
    } | null;
    contractUrl: {
        [activityId: string]: string | undefined,
    };
    /** 协议链接 */
    previewContractUrl: {
        [activityId: string]: string | undefined,
    };
    /** 账户余额 */
    account: Money;
    contractType: 'monitor' | 'common' | null;
}

const m: Module<PrizeState, RootState> = {
    namespaced: true,
    state: {
        nav: '',
        mpVisible: false,
        withdrawType: 'yunzhanghu',
        accountInfo: null,
        prizeDetail: {
            list: [],
            loaded: false,
            requesting: false,
        },
        withdrawDetail: {
            list: [],
            loaded: false,
            requesting: false,
        },
        checkContract: null,
        contractUrl: {},
        previewContractUrl: {},
        /** 账户余额 */
        account: 0,
        contractType: null,
    },
    mutations: {
        setNav(state, nav) {
            state.nav = nav;
        },
        setMPVisible(state, mpVisible) {
            state.mpVisible = mpVisible;
        },
        setWithdrawType(state, withdrawType) {
            state.withdrawType = withdrawType;
        },
        /**
         * 记录账号信息
         */
        setAccountInfo(state, accountInfo) {
            state.accountInfo = accountInfo;
        },
        /**
         * 添加prizeDetail
         */
        concatPrizeDetail(state, list) {
            state.prizeDetail.list = state.prizeDetail.list.concat(list);
        },
        /**
         * 记录prizeDetail的req
         */
        setPrizeDetailReq(state, req) {
            state.prizeDetail.requesting = req;
        },
        /**
         * 记录prizeDetail的loaded
         */
        setPrizeDetailLoaded(state, loaded) {
            state.prizeDetail.loaded = loaded;
        },
        /**
         * 添加withdrawDetail
         */
        concatWithdrawDetail(state, list) {
            state.withdrawDetail.list = state.withdrawDetail.list.concat(list);
        },
        /**
         * 记录withdrawDetail的req
         */
        setWithdrawDetailReq(state, req) {
            state.withdrawDetail.requesting = req;
        },
        /**
         * 记录withdrawDetail的loaded
         */
        setWithdrawDetailLoaded(state, loaded) {
            state.withdrawDetail.loaded = loaded;
        },
        /**
         * 记录check Contract数据
         */
        setCheckContract(state, checkContract) {
            state.checkContract = checkContract;
        },
        setContractUrl(state, { activityId, contractUrl }) {
            Vue.set(state.contractUrl, activityId, contractUrl);
        },
        /**
         * 记录协议链接
         */
        setPreviewContractUrl(state, { activityId, contractUrl }) {
            Vue.set(state.previewContractUrl, activityId, contractUrl);
        },
        /**
         * 记录账户余额
         */
        setAccount(state, money) {
            state.account = money;
        },
        setContractType(state, contractType) {
            state.contractType = contractType;
        },
    },
    actions: {
        /**
         * 提现请求
         */
        async createContract({ state, dispatch }, { activityId, agreementRead }) {
            const response = await Vue.axios.post('/api/v1/prize/create_contract', {
                id: activityId,
                agreement_read: agreementRead,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 提现请求
         */
        async request({ state, dispatch }, { accountType, account, channel, finish_face_auth }) {
            const response = await Vue.axios.post('/api/v1/prize/withdraw', {
                type: accountType,
                account,
                channel,
                finish_face_auth
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                dispatch({
                    type: 'getAccount',
                    forceUpdate: true,
                });
            }
            return rdata;
        },
        // 人脸认证
        async face({ state, dispatch }, redirect_url) {
            const response = await Vue.axios.get('/api/v1/prize/youfu/face_auth_url', {
                params: {redirect_url}
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取账号信息
         */
        async getAccountInfo({ state, commit }, { forceUpdate } = {}) {
            if (state.accountInfo && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/prize/withdraw_method');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setAccountInfo', rdata.data);
            }
            return rdata;
        },
        /**
         * 更新账户信息
         */
        async updateAccount({ state, dispatch }, { accountType, account }) {
            const response = await Vue.axios.post('/api/v1/prize/withdraw_method', {
                type: accountType,
                account,
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                dispatch('getAccountInfo', {
                    forceUpdate: true,
                });
            }
            return rdata;
        },
        /**
         * 奖金明细
         */
        async getPrizeDetail({ state, commit }, { offset, count, forceUpdate }) {
            if (offset === 0 && forceUpdate) {
                state.prizeDetail = {
                    list: [],
                    loaded: false,
                    requesting: false,
                };
            }
            const wrap = state.prizeDetail;
            if (wrap.loaded || wrap.requesting) {
                return;
            }
            const end = offset + count;
            if (end <= wrap.list.length) {
                return;
            }
            if (offset < wrap.list.length) {
                offset = wrap.list.length;
                count = end - offset;
            }
            commit('setPrizeDetailReq', true);
            const response = await Vue.axios.get('/api/v1/prize/prize_detail', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatPrizeDetail', rdata.data);
                if (rdata.data.length < count) {
                    commit('setPrizeDetailLoaded', true);
                }
            }
            commit('setPrizeDetailReq', false);
            return rdata;
        },
        /**
         * 提现明细
         */
        async getWithdrawDetail({ state, commit }, { offset, count, forceUpdate }) {
            if (offset === 0 && forceUpdate) {
                state.withdrawDetail = {
                    list: [],
                    loaded: false,
                    requesting: false,
                };
            }
            const wrap = state.withdrawDetail;
            if (wrap.loaded || wrap.requesting) {
                return;
            }
            const end = offset + count;
            if (end <= wrap.list.length) {
                return;
            }
            if (offset < wrap.list.length) {
                offset = wrap.list.length;
                count = end - offset;
            }
            commit('setWithdrawDetailReq', true);
            const response = await Vue.axios.get('/api/v1/prize/withdraw_detail', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('concatWithdrawDetail', rdata.data);
                if (rdata.data.length < count) {
                    commit('setWithdrawDetailLoaded', true);
                }
            }
            commit('setWithdrawDetailReq', false);
            return rdata;
        },
        async checkContract({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/prize/check_contract');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setCheckContract', rdata.data);
            }
            return rdata;
        },
        /**
         * 获得协议链接
         */
        async getContractUrl({ state, commit }, { activityId }) {
            const response = await Vue.axios.get('/api/v1/prize/contract_sign_url', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setContractUrl', {
                    activityId,
                    contractUrl: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取账户余额
         */
        async getAccount({ state, commit }, { forceUpdate } = {}) {
            const response = await Vue.axios.get('/api/v1/prize/account');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setAccount', rdata.data);
            }
            return rdata;
        },
        async getContractStatus(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/prize/contract_sign_status', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获得合同链接
         */
        async getPreviewContractUrl({ commit }, { activityId }) {
            const response = await Vue.axios.get('/api/v1/prize/contract_view_url', {
                params: {
                    id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setPreviewContractUrl', {
                    activityId,
                    contractUrl: rdata.data,
                });
            }
            return rdata;
        },
        async getContractType({ state, commit }, { id, forceUpdate } = {}) {
            if (state.contractType && !forceUpdate) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/prize/contract_type', {
                params: {
                    id,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setContractType', rdata.data);
            }
            return rdata;
        },
        async getWithdrawType({ commit }) {
            const response = await Vue.axios.get('/api/v1/prize/withdraw_channel');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setWithdrawType', rdata.data === '身边云' ? 'shenbianyun' : 'yunzhanghu');
            }
            return rdata;
        },
        /**
         * 获取身边云签约信息
         */
        async youfuSign({ commit }) {
            const response = await Vue.axios.get('/api/v1/prize/youfu/sign');
            const rdata = response.data;
            return rdata;
        },
        /**
         * 转跑币
         */
        async convertCoin(context, count) {
            const response = await Vue.axios.post('/api/v1/prize/convert_coin', { count });
            const rdata = response.data;
            return rdata;
        },
    },
};

export default m;
